import { useEffect, useMemo, useRef, useState } from "react";
import { useThree, useFrame } from "@react-three/fiber";
import { useRoute, useLocation } from "wouter";
import gsap from "gsap";
import * as THREE from "three";
import { pages } from "./data/pageData";
import { PhotoCube } from "./objects/PhotoCube";
import Pillar from "./objects/Pillar";
import { GrassPlane } from "./objects/GrassPlane";
import { CloudShadows } from "./objects/Clouds";

const DEFAULT_PROJECT_ID = "portfolio-site";
const HALF_PI = Math.PI / 2;
const TWO_PI = Math.PI * 2;
const SINK_MAX = 1.3;
const ANIMATION_DURATION = 0.8;
const ANIMATION_EASE = "power2.out";

// helpers
function getLastProjectId(): string {
    return sessionStorage.getItem("lastProjectId") ?? DEFAULT_PROJECT_ID;
}

function setLastProjectId(id: string) {
    sessionStorage.setItem("lastProjectId", id);
}

function scaleOr(value: number | undefined, fallback = 1.0): number {
    return value ?? fallback;
}

function nearestAlignedRotation(current: number, targetBase = 0): number {
    const nearest = Math.round((current - targetBase) / TWO_PI) * TWO_PI;
    return targetBase + nearest;
}

// ═════════════════════════════════════════════════════════════════════
// Scene
// ═════════════════════════════════════════════════════════════════════
export function Scene() {
    return (
        <group>
            <ambientLight intensity={0.3} />
            <directionalLight
                position={[1.75, 2, 3]}
                intensity={1.0}
                castShadow
                shadow-mapSize={[2048, 2048]}
                shadow-camera-far={50}
                shadow-camera-left={-20}
                shadow-camera-right={20}
                shadow-camera-top={20}
                shadow-camera-bottom={-20}
                shadow-bias={-0.001}
            />
            <directionalLight position={[0, 0, -1]} intensity={0.1} />

            {/* Project cubes */}
            {pages.map((project) => (
                <PageCube key={project.id} {...project} />
            ))}

            <GrassPlane position={[-5.0, -2.5, -5.0]} size={[65.0, 65.0]} />
            {/* <CloudShadows /> */}

            <CameraRig otherFocus={[0, 0, 0]} />
        </group>
    );
}





interface PageCubeProps {
    id: string;
    position: [number, number, number];
    imageScale: number;
    image: string;
    mapScale?: number;
}

function PageCube({
    id,
    position,
    imageScale,
    image,
    mapScale,
}: PageCubeProps) {
    const [, setLocation] = useLocation();
    const [isFocus, focusParams] = useRoute("/projects/:id");
    const [isEntered, enteredParams] = useRoute("/projects/:id/info");

    const groupRef = useRef<THREE.Group>(null);
    const whiteCubeRef = useRef<THREE.Mesh>(null);

    const animatedValues = useRef({
        sinkAmount: 0,
        cubeScale: scaleOr(mapScale),
    });

    const isHovered = useRef(false);
    const isSelected =
        (isFocus && focusParams?.id === id) ||
        (isEntered && enteredParams?.id === id);

    const targetSink = useMemo(() => {
        if (isSelected) return 0;

        const selectedId = focusParams?.id ?? enteredParams?.id;
        const selectedProject = selectedId
            ? pages.find((p) => p.id === selectedId)
            : null;
        if (!selectedProject) return 0;

        const selectedPos = new THREE.Vector3(...selectedProject.position);
        const thisPos = new THREE.Vector3(...position);
        const distance = selectedPos.distanceTo(thisPos);

        const baseSink = SINK_MAX;
        const enteredExtra = isEntered ? 0.25 : 0.0;
        return baseSink + enteredExtra;
    }, [
        isFocus,
        isEntered,
        focusParams?.id,
        enteredParams?.id,
        isSelected,
        position,
    ]);

    // animate sink
    useEffect(() => {
        const scale = scaleOr(mapScale);

        gsap.to(animatedValues.current, {
            duration: ANIMATION_DURATION,
            sinkAmount: targetSink,
            cubeScale: (isSelected ? 1.15 : 1.0) * scale,
            ease: ANIMATION_EASE,
            overwrite: "auto",
        });
    }, [targetSink, isSelected, isHovered, mapScale]);


    // animate white cube selection
    useFrame(() => {
        // if (whiteCubeRef.current)
        if (whiteCubeRef.current) {
            whiteCubeRef.current.scale.setScalar(
                animatedValues.current.cubeScale,
            );
        }
        if (groupRef.current) {
            groupRef.current.position.y =
                position[1] - animatedValues.current.sinkAmount;
        }
    });

    const scale = scaleOr(mapScale);

    return (
        <group>
            {/* outer pillar base */}
            <Pillar
                scale={1.5 * scale}
                position={[position[0], position[1] - 2.11, position[2]]}
            />

            {/* cube and inner pillar */}
            <group ref={groupRef} position={position}>
                <group position={[0, scale - 1.0 || 0.1, 0]}>
                    <mesh
                        ref={whiteCubeRef}
                        onClick={(e) => {
                            e.stopPropagation();
                            setLocation(`/projects/${id}`);
                        }}
                        onPointerEnter={() => {
                            document.body.style.cursor = "pointer";
                        }}
                        onPointerLeave={() => {
                            document.body.style.cursor = "default";
                        }}
                        castShadow
                        receiveShadow
                    >
                        <boxGeometry />
                        <meshBasicMaterial
                            color={0xeeeeee}
                            side={THREE.BackSide}
                        />
                    </mesh>

                    <group scale={scale}>
                        <PhotoCube
                            projectId={id}
                            scale={imageScale * scale}
                            texturePath={image}
                            position={[0, 0, 0]}
                        />
                    </group>
                </group>

                <Pillar position={[0, -0.6, 0]} scale={1.3 * scale} />
            </group>
        </group>
    );
}



type CameraMode = "other" | "overview" | "focus" | "entered";

function CameraRig({ otherFocus }: { otherFocus: [number, number, number] }) {
    const { camera } = useThree();
    const [, setLocation] = useLocation();

    // const [isOther] = useRoute("/:other");
    const [isProjectsIndex] = useRoute("/projects");
    const [isFocus, focusParams] = useRoute("/projects/:id");
    const [isEntered, enteredParams] = useRoute("/projects/:id/info");

    // useEffect(() => {
    //     if (isProjectsIndex) {
    //         setLocation(`/projects/${getLastProjectId()}`, { replace: true });
    //     }
    // }, [isProjectsIndex, setLocation]);

    // store last visited project
    // useEffect(() => {
    //     const id = focusParams?.id ?? enteredParams?.id;
    //     if (id) setLastProjectId(id);
    // }, [focusParams?.id, enteredParams?.id]);

    // refs
    const cameraOffset = useRef(camera.position.clone());
    const lastMode = useRef<CameraMode>(
        isFocus ? "focus" : isEntered ? "entered" : isProjectsIndex ? "overview" : "other",
    );
    const isColdLoad = useRef(true);

    const rigData = useRef({
        position: camera.position.clone(),
        focus: new THREE.Vector3(),
        rotation: 0,
        realRotation: 0,
    });

    // animate camera based on state
    useEffect(() => {
        const targetPos = new THREE.Vector3();
        const targetFocus = new THREE.Vector3();
        let moveTime = 1.0;
        let ease = "power2.inOut";

        let currentMode: CameraMode = "other";
        if (isEntered && enteredParams?.id) currentMode = "entered";
        else if (isFocus && focusParams?.id) currentMode = "focus";
        else if (isProjectsIndex) currentMode = "overview";

        console.log(isEntered, isFocus, isProjectsIndex)
        console.log(currentMode);

        const exitingFromCube =
            lastMode.current === "entered" && currentMode !== "entered";

        if (currentMode === "focus" && focusParams?.id) {
            const project = pages.find((p) => p.id === focusParams.id);
            if (project) {
                targetPos.set(...project.position).add(cameraOffset.current);
                targetFocus.set(...project.position);

                if (exitingFromCube) {
                    moveTime = 1.0;
                    ease = "elastic.out(0.7,0.5)";
                } else if (lastMode.current === "focus") {
                    moveTime = isColdLoad.current ? 0 : 0.75;
                    ease = "power4.out";
                } else {
                    // coming from elsewhere (home page)
                    rigData.current.rotation = nearestAlignedRotation(
                        rigData.current.rotation,
                        0,
                    );
                    moveTime = 1.0;
                    ease = "elastic.out(0.4,0.3)";
                }
            } else {
                // setLocation(`/projects/${DEFAULT_PROJECT_ID}`);
            }
        } else if (currentMode === "entered" && enteredParams?.id) { // do i need this &&
            const project = pages.find((p) => p.id === enteredParams.id);
            if (project) {
                targetPos.set(...project.position);
                targetFocus.set(...project.position);
                moveTime = isColdLoad.current ? 0 : 1.4;
                ease = "back.in";
            }
        } else if (currentMode === "overview") {
            const overviewPos: [number, number, number] = [-3.0, 15.0, -3.0];

            targetPos.set(...overviewPos)
            // targetPos.y += cameraOffset.current.y;
            targetFocus.set(...overviewPos);
            targetPos.x += 1.01;
            targetFocus.y = 0.0;
            targetPos.z += 1.01;
            moveTime = isColdLoad.current ? 0 : 1.0;
            ease = "power4.out";
        } else {
            // home pages
            targetPos.set(...otherFocus).add(cameraOffset.current);
            // targetFocus.set(...otherFocus);
            moveTime = isColdLoad.current ? 0 : 1.0;
            ease = "elastic.out(1.2,1.0)";
        }

        if (currentMode != 'other') {
            gsap.to(rigData.current.position, {
                duration: moveTime,
                x: targetPos.x,
                y: targetPos.y,
                z: targetPos.z,
                ease,
                overwrite: true,
            });

            gsap.to(rigData.current.focus, {
                duration: moveTime,
                x: targetFocus.x,
                y: targetFocus.y,
                z: targetFocus.z,
                ease,
                overwrite: true,
            });
        }

        isColdLoad.current = false;
        lastMode.current = currentMode;
    }, [isFocus, isEntered, focusParams?.id, enteredParams?.id, isProjectsIndex]);

    // camera rotation
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            const key = e.key.toUpperCase();
            if (key === "A" || key === "ARROWLEFT")
                rigData.current.rotation -= HALF_PI;
            if (key === "D" || key === "ARROWRIGHT")
                rigData.current.rotation += HALF_PI;
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);


    useFrame((state, delta) => {
        const {
            position: basePos,
            focus: focusPoint,
            realRotation: currentRot,
        } = rigData.current;
        let targetRot = rigData.current.rotation;

        // lil sway
        if (lastMode.current === "other") {
            targetRot += Math.sin(state.clock.elapsedTime * 0.12) * 0.3;
        }

        const smoothRot = THREE.MathUtils.lerp(
            currentRot,
            targetRot,
            1 - Math.exp(-4 * delta),
        );
        rigData.current.realRotation = smoothRot;

        const offset = new THREE.Vector3()
            .subVectors(basePos, focusPoint)
            .applyAxisAngle(new THREE.Vector3(0, 1, 0), smoothRot);

        camera.position.copy(focusPoint).add(offset);
        camera.lookAt(focusPoint);
    });

    return null;
}